<template>
  <div class="w-full flex flex-col">
    <div class="graph-area w-full pl-4 pr-16 flex min-w-0 min-h-0 overflow-y-hidden"
    :style="{ height: showAllGroups ? animHeights.open : animHeights.closed }">
      <!-- Platform Position Labels -->
      <div class="flex flex-col gap-3 pr-4 flex-shrink-0">
        <div v-for="(label, index) in platformPositionLabels" :key="`position-label-${index}`"
        class="flex items-center gap-1.5 py-2.5" :style="{height: `${groupRowHeight}px`}">
          <img v-if="label?.icon" :src="label.icon" class="w-4 h-4" />
          <OtherIcon v-else :width="16" :height="16" class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ label.text }}
          </BaseText>
        </div>
      </div>
      <!-- Bars -->
      <div ref="barArea" class="flex-grow flex flex-col gap-3">
        <div v-for="(group, groupIndex) in platformBarGroups" :key="`bar-group-${groupIndex}`" 
        class="w-full flex flex-col justify-center gap-2 flex-shrink-0" :style="{height: `${groupRowHeight}px`}">
          <div v-for="(bar, barIndex) in group" :key="`bar-${groupIndex}-${barIndex}`"
          class="bar-elm" :style="{ '--bar-width': bar.widthPercent, backgroundColor: barColors[barIndex]}">
            <BaseText type="label" size="xs" class="bar-label text-text-muted">
              {{ bar.label }}
            </BaseText>
          </div>
        </div>
      </div>
    </div>
    <!-- See More Button -->
    <transition name="show-more">
      <div v-if="showExpandButton" class="w-full px-2.5 h-10 flex items-end overflow-y-hidden">
        <button class="px-1.5 py-1 rounded-md transition-colors duration-100 hover:bg-neutral-25"
        @click="() => { if (collapseOverflow) showAllGroups = !showAllGroups }">
          <BaseText type="label" size="sm" class="text-text-muted">
            See {{ showAllGroups ? 'Less' : 'More' }}
          </BaseText>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import formatLabelString from '@/utils/lens/formatGraphLabelString'

import OtherIcon from '@/components/globals/Icons/FilterIcons/Niches/OtherNicheIcon.vue'

export default {
  name: 'HorizontalMetricBarGraph',
  components: {
    OtherIcon
  },
  props: {
    selectedMetrics: {
      type: Array,
      required: true
    },
    dataGroups: {
      type: Array,
      required: true
    },
    currencyRegion: {
      type: String,
      default: 'USD'
    },
    barColors: {
      type: Array,
      default: () => ['#FFAA51', '#00B78F']
    },
    collapseOverflow: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      showAllGroups: !this.collapseOverflow
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    platformPositionLabels () {
      return this.dataGroups?.map(group => ({
        text: this.formatPositionLabel(group.label.text),
        icon: group.label.icon
      }))
    },
    platformBarGroups () {
      const maxValues = Array(this.dataGroups?.[0]?.values?.length ?? 0).fill(0).map((_, index) => {
        return Math.max(...this.dataGroups.map(group => group.values[index]))
      })
      return this.dataGroups.map(group => {
        return group.values.map((value, index) => {
          const metricType = this.getMetricLookup?.[this.selectedMetrics?.[index]]?.type ?? 'number'

          const widthPercent = value ? `${(value / maxValues[index] * 100).toFixed(2)}%` : '0%'
          const label = formatLabelString(value, metricType, this.currencyRegion)
          return { widthPercent, label }
        }) 
      })
    },
    // UI Computed values
    groupRowHeight () {
      return 8 * this.platformBarGroups?.[0]?.length + 4 * (this.platformBarGroups?.[0]?.length - 1) + 16
    },
    showExpandButton () {
      return this.collapseOverflow && this.dataGroups.length > 6
    },
    animHeights () {
      const numRows = this.dataGroups?.length ?? 0
      return {
        closed: `${this.groupRowHeight * Math.min(numRows, 5) + Math.min(numRows - 1, 4) * 12}px`,
        open: `${this.groupRowHeight * numRows + 12 * (numRows - 1)}px`
      }
    }
  },
  methods: {
    formatPositionLabel (labelStr) {
      // We will modify some of the labels to make them more readable
      if (labelStr === 'An Classic') return 'AN Classic'
      if (labelStr.includes('Instagram')) return labelStr.replace(/Instagram/g, '')
      if (labelStr.includes('Facebook')) return labelStr.replace(/Facebook/g, '')
      return labelStr
    }
  }
}
</script>

<style scoped>
.graph-area {
  transition: height 0.5s ease-in-out;
}
.bar-elm {
  position: relative;
  height: 8px;
  border-radius: 3px;
  min-width: 2px;
  width: var(--bar-width);
  animation: barEnter 0.75s ease-out forwards;
}
@keyframes barEnter {
  from { width: 2px; }
  to { width: var(--bar-width); }
}
.bar-label {
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  z-index: 5; 
}

.show-more-leave-active {
  animation: showMoreExit 0.5s ease-in-out forwards;
}
@keyframes showMoreExit {
  from {
    opacity: 1;
    height: 40px;
  }
  to {
    opacity: 0;
    height: 0px;
  }
}
</style>